'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Menu, useTheme } from '@mui/material';
import { MouseEvent } from 'react';

import { NavLink } from './app-header-nav';
import { AppHeaderNavSmUpMenuItems } from './app-header-nav-sm-up-menu-items';
import styles from './app-header-nav-sm-up.module.scss';
import { useAppHeaderNavController } from './use-app-header-nav-controller';

const MENU_LEFT_ACCENT_PX = 4;

// eslint-disable-next-line max-statements
export const AppHeaderNavSmUp = ({ navLinks, testId }: Readonly<{ navLinks: NavLink[]; testId?: string }>) => {
  const theme = useTheme();
  const primaryMainColor = theme.palette.primary.main;
  const {
    closeDropdown,
    menuAnchor,
    onMenuClose,
    onMenuSurfaceMouseEnter,
    onMenuSurfaceMouseLeave,
    onSubmenuClick,
    onSubmenuMouseEnter,
    onSubmenuMouseLeave,
    onTriggerClick,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    openMenuLabel,
    openSubmenus,
    setMenuPaperRef,
    setTriggerRef,
  } = useAppHeaderNavController();

  return (
    <Box
      className={styles.AppHeaderNavSmUp}
      color={theme.palette.grey[900]}
      data-testid={testId}
    >
      {navLinks.map(({ children, isActive = false, isDisabled = false, label, onClick = undefined }) => {
        if (!children?.length) {
          return (
            <Button
              color="inherit"
              data-testid={testId && `${testId}-navLink-${label}`}
              disabled={Boolean(isDisabled && !isActive)}
              key={label}
              onClick={onClick}
              style={{
                color: isActive ? primaryMainColor : 'inherit',
              }}
            >
              {label}
            </Button>
          );
        }

        const isOpen = openMenuLabel === label;
        const isHighlighted = isActive || isOpen;

        return (
          <Box
            className={styles.dropdownContainer}
            key={label}
          >
            <Button
              color="inherit"
              data-testid={testId && `${testId}-navLink-${label}`}
              disabled={isDisabled}
              endIcon={isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              ref={(element) => {
                setTriggerRef(label, element);
              }}
              onClick={(event) => {
                onTriggerClick(event, label, children, isOpen);
              }}
              onMouseEnter={(event) => {
                onTriggerMouseEnter(event, label, children);
              }}
              onMouseLeave={(event) => {
                onTriggerMouseLeave(event);
              }}
              onMouseMove={onMenuSurfaceMouseEnter}
              style={{
                color: isHighlighted ? primaryMainColor : 'inherit',
                cursor: 'pointer',
              }}
            >
              {label}
            </Button>
            <Menu
              anchorEl={menuAnchor}
              data-testid={testId && `${testId}-navMenu-${label}`}
              disableAutoFocusItem
              disableScrollLock
              hideBackdrop
              MenuListProps={{
                onMouseEnter: onMenuSurfaceMouseEnter,
                onMouseLeave: (event: MouseEvent<HTMLElement>) => {
                  onMenuSurfaceMouseLeave(event, label);
                },
                sx: {
                  borderLeft: `${MENU_LEFT_ACCENT_PX}px solid ${primaryMainColor}`,
                  paddingBottom: theme.spacing(1),
                  paddingTop: theme.spacing(1),
                },
              }}
              onClose={() => onMenuClose(label)}
              open={isOpen}
              slotProps={{
                paper: {
                  onMouseEnter: onMenuSurfaceMouseEnter,
                  onMouseLeave: (event: MouseEvent<HTMLElement>) => {
                    onMenuSurfaceMouseLeave(event, label);
                  },
                  ref: (element: HTMLElement | null) => {
                    setMenuPaperRef(label, element);
                  },
                  sx: {
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                    boxShadow: '0 1px 7px 0 rgba(0, 0, 0, 0.10)',
                    pointerEvents: 'auto',
                  },
                },
                root: {
                  sx: {
                    pointerEvents: 'none',
                  },
                },
              }}
            >
              <AppHeaderNavSmUpMenuItems
                links={children}
                onLeafClick={(onLeafItemClick) => {
                  onLeafItemClick?.();
                  closeDropdown();
                }}
                onSubmenuClick={onSubmenuClick}
                onSubmenuMouseEnter={onSubmenuMouseEnter}
                onSubmenuMouseLeave={onSubmenuMouseLeave}
                openSubmenus={openSubmenus}
                testId={testId}
              />
            </Menu>
          </Box>
        );
      })}
    </Box>
  );
};
